'use client';

import GroupBuySearchBar from '@/components/features/group-buy/GroupBuySearchBar';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  PlusCircle,
  Users,
  ChevronLeft,
  ChevronRight,
  ShoppingBag,
  ArrowUpDown,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import GroupBuyCard from '@/components/features/group-buy/GroupBuyCard';
import { useRouter } from 'next/navigation';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import Link from 'next/link';
import { useAuth, useClerk } from '@clerk/nextjs';
import type { Doc } from '@/convex/_generated/dataModel';
import GroupBuyFilterSidebar from '@/components/filters/GroupBuyFilterSidebar';
import GroupBuyMobileFilterDrawer from '@/components/filters/GroupBuyMobileFilterDrawer';
import { useGroupBuyStore } from '@/stores/useGroupBuyStore';
import { useGroupBuyUrlSync } from '@/hooks/useGroupBuyUrlSync';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const ITEMS_PER_PAGE = 12;

// Allowed status values
const allowedStatuses = ['active', 'completed', 'cancelled'] as const;
type StatusType = (typeof allowedStatuses)[number];

const sortOptions = [
  { value: 'date_desc', label: 'Latest', Icon: ArrowUpDown },
  { value: 'date_asc', label: 'Oldest', Icon: ArrowUpDown },
  { value: 'price_asc', label: 'Price: Low-High', Icon: ArrowUpDown },
  { value: 'price_desc', label: 'Price: High-Low', Icon: ArrowUpDown },
  { value: 'participants_desc', label: 'Most Participants', Icon: Users },
];

export default function GroupBuysPageClient() {
  const { userId } = useAuth();
  const { openSignIn } = useClerk();
  const router = useRouter();

  // Get values and actions from Zustand store
  const {
    searchTerm,
    productType,
    status,
    sortBy,
    priceRange,
    targetQuantityRange,
    progressRange,
    endingTimeframe,
    endingDate,
    currentPage,
    setCurrentPage,
    startBatchUpdate,
    endBatchUpdate,
    setSortBy,
  } = useGroupBuyStore();

  // Sync URL with store state
  useGroupBuyUrlSync();

  // State for pagination
  const [paginationOpts, setPaginationOpts] = useState<any>({ numItems: ITEMS_PER_PAGE });
  const [displayedGroupBuys, setDisplayedGroupBuys] = useState<Doc<'groupBuys'>[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Create a ref to track active filters for dependency tracking
  const filtersRef = useRef({
    searchTerm,
    productType,
    status,
    sortBy,
    priceRange,
    targetQuantityRange,
    progressRange,
    endingTimeframe,
    endingDate,
  });

  // Update the ref when filters change
  useEffect(() => {
    filtersRef.current = {
      searchTerm,
      productType,
      status,
      sortBy,
      priceRange,
      targetQuantityRange,
      progressRange,
      endingTimeframe,
      endingDate,
    };
  }, [
    searchTerm,
    productType,
    status,
    sortBy,
    priceRange,
    targetQuantityRange,
    progressRange,
    endingTimeframe,
    endingDate,
  ]);

  // Prepare query arguments based on store state
  const narrowedStatus = allowedStatuses.includes(status as StatusType)
    ? (status as StatusType)
    : undefined;
  const queryArgs = useMemo(
    () => ({
      searchTerm: searchTerm || undefined,
      productType: productType || undefined,
      status: narrowedStatus,
      sortBy,
      priceMin: priceRange.min,
      priceMax: priceRange.max,
      targetQuantityMin: targetQuantityRange.min,
      targetQuantityMax: targetQuantityRange.max,
      progressMin: progressRange.min,
      progressMax: progressRange.max,
      endingTimeframe,
      endingDate: endingDate?.toISOString(),
      paginationOpts,
      pageSize: ITEMS_PER_PAGE,
    }),
    [
      searchTerm,
      productType,
      narrowedStatus,
      sortBy,
      priceRange.min,
      priceRange.max,
      targetQuantityRange.min,
      targetQuantityRange.max,
      progressRange.min,
      progressRange.max,
      endingTimeframe,
      endingDate,
      paginationOpts,
    ],
  );

  // Fetch data using the query args
  const result = useQuery(api.groupBuys.getWithFilters, queryArgs);

  // Handle results and update state
  useEffect(() => {
    if (result) {
      setDisplayedGroupBuys(result.groupBuys);
      setTotalCount(result.totalCount || 0);
      setTotalPages(Math.ceil((result.totalCount || 0) / ITEMS_PER_PAGE));
      setIsLoadingMore(false);
    }
  }, [result]);

  // Effect to reset pagination when filters change - use the ref to avoid multiple requests
  useEffect(() => {
    const currentFilters = filtersRef.current;

    if (
      searchTerm !== currentFilters.searchTerm ||
      productType !== currentFilters.productType ||
      status !== currentFilters.status ||
      sortBy !== currentFilters.sortBy ||
      priceRange.min !== currentFilters.priceRange.min ||
      priceRange.max !== currentFilters.priceRange.max ||
      targetQuantityRange.min !== currentFilters.targetQuantityRange.min ||
      targetQuantityRange.max !== currentFilters.targetQuantityRange.max ||
      progressRange.min !== currentFilters.progressRange.min ||
      progressRange.max !== currentFilters.progressRange.max ||
      endingTimeframe !== currentFilters.endingTimeframe ||
      endingDate !== currentFilters.endingDate
    ) {
      // Only reset pagination if filters actually changed
      setPaginationOpts({ numItems: ITEMS_PER_PAGE });
    }
  }, [
    searchTerm,
    productType,
    status,
    sortBy,
    priceRange,
    targetQuantityRange,
    progressRange,
    endingTimeframe,
    endingDate,
  ]);

  // Handle page change with rate limiting
  const handlePageChange = useCallback(
    (newPage: number) => {
      // Prevent excessive pagination
      const lastPageChange = sessionStorage.getItem('lastPageChange');
      const now = Date.now();
      if (lastPageChange && now - parseInt(lastPageChange, 10) < 1000) {
        return; // Prevent rapid page changes
      }
      sessionStorage.setItem('lastPageChange', now.toString());

      // Use batch update to prevent multiple requests
      startBatchUpdate();
      setCurrentPage(newPage);
      // Calculate skip based on page number
      const skipItems = (newPage - 1) * ITEMS_PER_PAGE;
      setPaginationOpts({
        numItems: ITEMS_PER_PAGE,
        skipItems,
      });
      endBatchUpdate();
    },
    [setCurrentPage, startBatchUpdate, endBatchUpdate],
  );

  // Handle sort change
  const handleSortChange = useCallback(
    (value: string) => {
      setCurrentPage(1); // Reset to first page on sort change
      setSortBy(value);
    },
    [setSortBy, setCurrentPage],
  );

  // Check if any filters are active
  const hasActiveFilters = () => {
    return (
      !!searchTerm ||
      !!productType ||
      (status && status !== 'active') ||
      priceRange.min !== undefined ||
      priceRange.max !== undefined ||
      targetQuantityRange.min !== undefined ||
      targetQuantityRange.max !== undefined ||
      progressRange.min !== undefined ||
      progressRange.max !== undefined ||
      !!endingTimeframe ||
      !!endingDate
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container px-4 py-6 sm:py-8 lg:py-10 max-w-screen-xl mx-auto">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          {/* Desktop Filter Sidebar - Hidden on Mobile */}
          <div className="hidden md:block w-full md:w-64 lg:w-72 shrink-0">
            <GroupBuyFilterSidebar className="sticky top-24" />
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            {/* Search and Sort Area */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1">
                <GroupBuySearchBar />
              </div>
              {/* New container for Sort and Filter buttons */}
              <div className="flex flex-row gap-2 w-full sm:w-auto items-center">
                <div className="flex-1">
                  {' '}
                  {/* Sort Dropdown takes available space */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full flex items-center justify-between gap-1.5"
                      >
                        <span>
                          {sortOptions.find((option) => option.value === sortBy)?.label ||
                            'Sort By'}
                        </span>
                        <ArrowUpDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[200px]">
                      {sortOptions.map((option) => (
                        <DropdownMenuItem
                          key={option.value}
                          onClick={() => handleSortChange(option.value)}
                          className="flex items-center gap-2"
                        >
                          <option.Icon className="h-4 w-4" />
                          <span>{option.label}</span>
                          {sortBy === option.value && <Check className="h-4 w-4 ml-auto" />}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex-shrink-0">
                  {' '}
                  {/* Container for MobileFilterDrawer */}
                  <GroupBuyMobileFilterDrawer />
                </div>
              </div>
            </div>

            {/* Results Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">
                  <span className="text-muted-foreground">Group Buys</span>
                </h1>
                {hasActiveFilters() && (
                  <div className="text-sm text-muted-foreground mt-1">Filtered results</div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                {userId && (
                  <Link href="/group-buys/my-group-buys" className="w-full sm:w-auto">
                    <Button
                      variant="outline"
                      className="w-full sm:w-auto flex items-center justify-center gap-2"
                    >
                      My Group Buys
                    </Button>
                  </Link>
                )}
                <Button
                  className="w-full sm:w-auto flex items-center justify-center gap-2"
                  onClick={() => {
                    if (userId) {
                      router.push('/group-buys/new');
                    } else {
                      openSignIn();
                    }
                  }}
                >
                  <PlusCircle className="w-4 h-4" /> Start a Group Buy
                </Button>
              </div>
            </div>

            {/* Loading State & Results */}
            {!result ? (
              <div className="flex justify-center py-16">
                <LoadingSpinner />
              </div>
            ) : displayedGroupBuys.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {displayedGroupBuys.map((groupBuy) => (
                  <GroupBuyCard key={groupBuy._id} groupBuy={groupBuy} currentUserId={userId} />
                ))}
              </div>
            ) : (
              <div className="text-center py-6 sm:py-10 lg:py-16 px-4 sm:px-8 bg-card rounded-xl border border-border">
                <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-medium text-foreground mb-2">No group buys found</h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Try adjusting your search or filters, or start a new group buy!
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-2 max-w-xs sm:max-w-none mx-auto">
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (userId) {
                        router.push('/group-buys/new');
                      } else {
                        openSignIn();
                      }
                    }}
                    className="flex items-center gap-2 w-full sm:w-auto"
                  >
                    Start a Group Buy
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 0 && displayedGroupBuys.length > 0 && (
          <div className="flex flex-col items-center justify-center gap-3 mt-8 mb-4">
            {/* Total count display */}
            {totalCount > 0 && (
              <div className="text-center mb-2">
                <span className="text-base font-medium">
                  {totalCount.toLocaleString()}{' '}
                  <span className="text-muted-foreground">
                    {totalCount === 1 ? 'Group Buy' : 'Group Buys'}
                  </span>
                </span>
              </div>
            )}
            {/* Pagination controls */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-2 w-full sm:w-auto"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                </div>
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-2 w-full sm:w-auto"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
